import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Outlet } from 'react-router-dom'
import { createContext, useContext, useEffect, useState } from 'react'
import LoadBig from '@/components/LoadBig/LoadBig'
import { useProfile } from '@/stores/useAuth.ts'
import { HeyProfile } from '@/models/profile.ts'
import { ProfAccount } from '@/models/profAccount.ts'
import { useClient } from '@/stores/useClient.ts'
import { HeyThemeConstantProvider, useHeyTheme } from '@/contexts/heyTheme.tsx'
import PortalSidebar from '@/components/portal/PortalSidebar.tsx'
import PortalTopBar from '@/components/portal/PortalTopBar.tsx'
import { PORTAL_NEUTRAL, PORTAL_PRIMARY, PORTAL_TEXT, PORTAL_BG, PORTAL_SIDEBAR_BG, PORTAL_BORDER } from '@/controller/driveDefaults.ts'
import UploadProgress from '@/components/UploadProgress.tsx'
import { useUploadQueue, isStatusFinalState } from '@/stores/useUploadQueue.ts'
import TutorialOverlay from '@/components/tutorial/TutorialOverlay.tsx'
import { useTutorial } from '@/contexts/tutorialContext.tsx'
import usePicFieldTitle from '@/hooks/usePicFieldTitle.ts'
import { setStatTrackingDisabled } from '@/controller/statsTracking.ts'
import { PORTAL_LOGIN_PATH } from '@/pages/portal/PortalAuth.tsx'

interface PortalContextType {
  account: ProfAccount | null
  isLoading: boolean
}

const PortalContext = createContext<PortalContextType | null>(null)

export function usePortalContext() {
  return useContext(PortalContext)
}

export default function PortalPage() {
  usePicFieldTitle()

  // The B2B surface must not produce events: the photographer browsing their own
  // content would otherwise show up in their own statistics. The preview iframes
  // are a separate document and rule themselves out via `_preview=1`.
  useEffect(() => {
    setStatTrackingDisabled(true)
    return () => setStatTrackingDisabled(false)
  }, [])

  return (
    <HeyThemeConstantProvider colorPrimary={PORTAL_PRIMARY} colorNeutral={PORTAL_NEUTRAL} colorContent={PORTAL_TEXT} font="Cormorant Infant">
      <PortalPageContent />
    </HeyThemeConstantProvider>
  )
}

function PortalPageContent() {
  const { setProfile, authenticationToken, setAuthenticationToken } = useProfile()
  const { getPortalMine, updatePortalMine } = useClient()
  const queryClient = useQueryClient()
  const setCompletedSeriesIds = useTutorial(s => s.setCompletedSeriesIds)
  const setOnTutorialComplete = useTutorial(s => s.setOnTutorialComplete)
  const [account, setAccount] = useState<ProfAccount | null>(null)
  const [isSidebarPinned, setIsSidebarPinned] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { color_primary, style: themeStyle } = useHeyTheme()
  const { uploadQueue } = useUploadQueue()
  const isUploading = uploadQueue.some(file => !isStatusFinalState(file.status))

  const { data: portalData, isLoading, error } = useQuery({
    queryKey: ['portal', 'mine'],
    queryFn: async () => {
      try {
        return await getPortalMine()
      } catch (e: any) {
        if (e?.response?.status === 401) {
          setAuthenticationToken(null)
          return null
        }
        throw e
      }
    },
    enabled: authenticationToken != null,
  })

  useEffect(() => {
    if (portalData?.data) {
      console.log('Portal data loaded:', portalData.data)
      const fetchedAccount: ProfAccount = portalData.data.account
      const fetchedProfile: HeyProfile = portalData.data.profile
      setCompletedSeriesIds(fetchedAccount.is_intro_done ? ['getting-started'] : [])
      setAccount(fetchedAccount)
      setProfile(fetchedProfile)
    }
  }, [portalData, setProfile, setCompletedSeriesIds])

  useEffect(() => {
    setOnTutorialComplete(async (seriesId) => {
      if (seriesId !== 'getting-started') return
      try {
        await updatePortalMine({ is_intro_done: true })
        queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      } catch (e) {
        console.error('Failed to persist tutorial completion', e)
      }
    })
    return () => setOnTutorialComplete(null)
  }, [setOnTutorialComplete, updatePortalMine, queryClient])

  // No token, or one the backend rejected (the query above clears it on 401):
  // there is nothing to load, so send people to the login instead of a
  // loading spinner that never resolves.
  if (!authenticationToken) {
    return <Navigate to={PORTAL_LOGIN_PATH} replace />
  }

  if (isLoading || !account) {
    return <LoadBig />
  }

  if (error) {
    return <div className="flex items-center justify-center h-screen">
      <p className="text-error">Fehler beim Laden des Portals</p>
    </div>
  }

  return (
    <>
      {color_primary && <meta name='theme-color' content={`#${color_primary}`} />}
      <style>{themeStyle} {`:root {
        --rounded-box: 0;
        --portal-bg: ${PORTAL_BG};
        --portal-sidebar-bg: ${PORTAL_SIDEBAR_BG};
        --portal-border: ${PORTAL_BORDER};
      }`}</style>
      <PortalContext.Provider value={{ account, isLoading }}>
        <div className="flex h-screen">
          {/* Desktop sidebar */}
          <div className="hidden md:block">
            <PortalSidebar isPinned={isSidebarPinned} onPinnedChange={setIsSidebarPinned} />
          </div>

          {/* Mobile sidebar drawer */}
          {isMobileMenuOpen && (
            <div className="fixed inset-0 z-50 md:hidden overflow-hidden touch-none">
              <div
                className="absolute inset-0 bg-[black] bg-opacity-60"
                onClick={() => setIsMobileMenuOpen(false)}
                onTouchMove={e => e.preventDefault()}
              />
              <div className="absolute left-0 top-0 h-full w-64 animate-slide-in-left">
                <PortalSidebar forceExpanded onNavigate={() => setIsMobileMenuOpen(false)} />
              </div>
            </div>
          )}

          <div className="flex-1 flex flex-col overflow-hidden">
            <PortalTopBar onMenuOpen={() => setIsMobileMenuOpen(true)} />
            <main className={`flex-1 overflow-auto bg-[var(--portal-bg)] ${isUploading ? 'pb-12' : ''}`}>
              <Outlet />
            </main>
          </div>
          {isUploading && (
            <div className="fixed bottom-0 left-0 right-0 z-50">
              <UploadProgress />
            </div>
          )}
        </div>
        <TutorialOverlay />
      </PortalContext.Provider>
    </>
  )
}
